import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Space, Typography, Spin, Result, Button } from 'antd';

import { goodsReceiptsApi } from './grnApi';
import { notifyError } from '../../../utils/notify';
import { extractApiError, readTextError } from '../../../utils/format';
import ReportToolbar from '../../../components/reports/ReportToolbar';
import { printIframe } from '../../../components/reports/ReportViewer';

// ─────────────────────────────────────────────────────────────────
// Glass-i — GRN Receipt viewer.
//
// Same design as the Proforma report viewer (pages/reports/
// ReportViewerPage): a Card with a meta header + ReportToolbar
// (back / zoom / fit-width / zoom / reload / print / email) over a
// grey scroll surface that shows the A4 sheet in a sandboxed iframe.
//
// Difference from the proforma flow: the GRN receipt is rendered
// synchronously (GET /api/erp/purchase/grns/:id/report → HTML), so
// there is no async job to poll — the HTML drops straight into the
// iframe srcDoc. Zoom is a CSS transform (no re-fetch), matching the
// proforma viewer's behaviour. The Jasper template is A4 portrait
// (210 × 297 mm) and the embedded @page CSS keeps print on A4.
// ─────────────────────────────────────────────────────────────────
export default function GoodsReceiptReportViewerPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [html, setHtml]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [zoom, setZoom]       = useState(1);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setHtml(null);
    try {
      const body = await goodsReceiptsApi.viewReport(id);
      setHtml(body);
    } catch (err) {
      const apiErr = readTextError(err);
      setError(apiErr ? apiErr : { code: 'UNKNOWN', message: extractApiError(err) });
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  function handlePrint() {
    const iframe = wrapRef.current && wrapRef.current.querySelector('iframe');
    if (!printIframe(iframe)) {
      notifyError(
        new Error('print failed'),
        'Could not open the print dialog. Try the browser menu → Print.'
      );
    }
  }

  return (
    <Card
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>GRN Receipt</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Goods Receipt Note
          </Typography.Text>
        </Space>
      }
      bodyStyle={{ padding: 0 }}
    >
      <ReportToolbar
        onBack={() => navigate(-1)}
        onZoomIn={() => setZoom((z) => Math.min(2.0, +(z + 0.1).toFixed(2)))}
        onZoomOut={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))}
        onFitWidth={() => setZoom(1)}
        onPrint={handlePrint}
        onRefresh={load}
        onEmail={() => {}}
        emailDisabled
        emailDisabledReason="Emailing GRN receipts isn't available yet."
      />
      <div ref={wrapRef} style={{ height: 800, borderTop: '1px solid #f0f0f0' }}>
        {loading ? (
          <div style={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spin tip="Loading GRN receipt…" />
          </div>
        ) : error ? (
          <div style={{ padding: 24 }}>
            <Result
              status="error"
              title={error.code || 'REPORT_RENDER_FAILED'}
              subTitle={error.message || 'Could not load the GRN receipt.'}
              extra={
                <Space>
                  <Button onClick={() => navigate(-1)}>Back</Button>
                  <Button type="primary" onClick={load}>Retry</Button>
                </Space>
              }
            />
          </div>
        ) : (
          <div style={{ width: '100%', height: '100%', overflow: 'auto', background: '#f0f2f5' }}>
            <iframe
              title="GRN Receipt"
              srcDoc={html || ''}
              sandbox="allow-same-origin allow-modals"
              style={{
                width:           `${100 / zoom}%`,
                height:          `${800 / zoom}px`,
                border:          'none',
                transform:       `scale(${zoom})`,
                transformOrigin: 'top left',
                background:      '#f0f2f5'
              }}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
